import { useAuth } from "@clerk/react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "@/features/api/apiClient";
import {
  getAdminMetricsOverview,
  type AdminMetricsOverviewResponse,
  type MetricsFilters,
  type MetricsRange,
} from "@/features/admin/adminApi";

// ============================================================================
// CONSTANTS
// ============================================================================

const RANGE_OPTIONS: { value: MetricsRange; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "all", label: "All time" },
  { value: "custom", label: "Custom" },
];

const FEATURE_OPTIONS = [
  { value: "", label: "All features" },
  { value: "blueprint_generation", label: "Blueprint generation" },
  { value: "launch_readiness_checklist", label: "Launch readiness checklist" },
  { value: "launch_readiness_assessment", label: "Launch readiness assessment" },
  { value: "monetisation_model_suggester", label: "Monetisation model" },
  { value: "sales_headline_generator", label: "Sales headline generator" },
];

const TIER_OPTIONS = [
  { value: "", label: "All tiers" },
  { value: "free", label: "Free" },
  { value: "standard", label: "Standard" },
  { value: "pro", label: "Pro" },
];

type Tab = "overview" | "ai" | "users" | "activity";

// ============================================================================
// HELPERS
// ============================================================================

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

function formatShortDate(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(iso));
}

function formatCost(value: string | null | undefined): string {
  if (!value) return "$0.000000";
  const n = Number(value);
  if (!Number.isFinite(n)) return "$0.000000";
  return `$${n.toFixed(6)}`;
}

function tierLabel(tier: string | null | undefined): string {
  if (tier === "lifetime_standard") return "Lifetime Standard";
  if (!tier) return "—";
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function featureLabel(feature: string): string {
  return FEATURE_OPTIONS.find((o) => o.value === feature)?.label ?? feature;
}

function statusBadge(status: string) {
  const cls =
    status === "success"
      ? "bg-emerald-100 text-emerald-800"
      : status === "failed"
        ? "bg-red-100 text-red-800"
        : "bg-amber-100 text-amber-800";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>
      {status}
    </span>
  );
}

// ============================================================================
// REUSABLE COMPONENTS
// ============================================================================

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-slate-950">{value}</p>
      {sub ? <p className="mt-1 text-xs text-slate-400">{sub}</p> : null}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-sm font-semibold text-indigo-700">{children}</h3>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <p className="rounded-md border border-dashed border-slate-200 p-4 text-sm text-slate-500">
      {message}
    </p>
  );
}

function CostDisclaimer() {
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
      AI costs are estimates based on stored token usage and pricing snapshots.
      They are not invoice reconciliation.
    </div>
  );
}

function AccessDenied() {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <p className="text-sm font-semibold text-slate-500">Not found</p>
      <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
        This page is not available
      </h2>
      <p className="mt-3 text-sm leading-6 text-slate-600">
        Metrics are only available to authorised operators.
      </p>
    </section>
  );
}

// ============================================================================
// TABS
// ============================================================================

function OverviewTab({ m }: { m: AdminMetricsOverviewResponse }) {
  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>Product overview</SectionTitle>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard label="Users" value={m.product.totalUsers} />
          <StatCard label="Projects" value={m.product.totalProjects} />
          <StatCard label="Blueprints" value={m.product.totalBlueprints} />
          <StatCard
            label="Generated blueprints"
            value={m.product.generatedBlueprints}
          />
          <StatCard label="Tool results" value={m.product.totalToolResults} />
          <StatCard label="Admin users" value={m.product.adminUsers} />
        </div>
        {m.product.usersByTier.length > 0 ? (
          <div className="mt-5">
            <p className="text-xs font-medium text-slate-500">Users by tier</p>
            <div className="mt-2 flex flex-wrap gap-3">
              {m.product.usersByTier.map((t) => (
                <div
                  key={t.tier}
                  className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                >
                  <span className="font-semibold text-slate-800">
                    {tierLabel(t.tier)}
                  </span>
                  <span className="ml-2 text-slate-500">{t.count}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>AI usage summary</SectionTitle>
        <CostDisclaimer />
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="AI calls (all time)"
            value={m.aiUsage.totalCalls}
          />
          <StatCard
            label={`AI calls (${m.range})`}
            value={m.aiUsage.callsInRange}
          />
          <StatCard
            label="Total tokens (all time)"
            value={m.aiUsage.totalTokens.toLocaleString()}
          />
          <StatCard
            label="Est. cost (all time)"
            value={formatCost(m.aiUsage.estimatedTotalCostUsd)}
            sub={`${formatCost(m.aiUsage.estimatedCostInRange)} in ${m.range}`}
          />
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>Access codes</SectionTitle>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total codes" value={m.accessCodes.totalCodes} />
          <StatCard label="Active codes" value={m.accessCodes.activeCodes} />
          <StatCard
            label="Total redemptions"
            value={m.accessCodes.totalRedemptions}
          />
          <StatCard
            label={`Redemptions (${m.range})`}
            value={m.accessCodes.redemptionsInRange}
          />
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>Purchase events</SectionTitle>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard label="Total events" value={m.purchases.totalEvents} />
          <StatCard
            label={`Events (${m.range})`}
            value={m.purchases.eventsInRange}
          />
        </div>
        {m.purchases.statusCounts.length > 0 ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {m.purchases.statusCounts.map((s) => (
              <div
                key={s.status}
                className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
              >
                <span className="font-semibold text-slate-800">{s.status}</span>
                <span className="ml-2 text-slate-500">{s.count}</span>
              </div>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function AiTab({ m }: { m: AdminMetricsOverviewResponse }) {
  return (
    <div className="space-y-6">
      <CostDisclaimer />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="AI calls (all time)"
          value={m.aiUsage.totalCalls}
        />
        <StatCard
          label={`Calls (${m.range})`}
          value={m.aiUsage.callsInRange}
        />
        <StatCard
          label="Total tokens (all time)"
          value={m.aiUsage.totalTokens.toLocaleString()}
          sub={`${m.aiUsage.inputTokens.toLocaleString()} in · ${m.aiUsage.outputTokens.toLocaleString()} out`}
        />
        <StatCard
          label="Est. cost (all time)"
          value={formatCost(m.aiUsage.estimatedTotalCostUsd)}
          sub={`${formatCost(m.aiUsage.estimatedCostInRange)} in ${m.range}`}
        />
      </div>

      {m.aiUsage.statusCounts.length > 0 ? (
        <div className="flex flex-wrap gap-3">
          {m.aiUsage.statusCounts.map((s) => (
            <div
              key={s.status}
              className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
            >
              <span className="font-semibold text-slate-800">{s.status}</span>
              <span className="ml-2 text-slate-500">{s.count}</span>
            </div>
          ))}
        </div>
      ) : null}

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>Cost by feature</SectionTitle>
        {m.aiUsage.featureBreakdown.length > 0 ? (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  {["Feature", "Calls", "Tokens", "Failed", "Avg tokens", "Avg cost", "Est. cost"].map((h) => (
                    <th key={h} className="py-2 pr-4 font-semibold text-slate-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {m.aiUsage.featureBreakdown.map((f) => (
                  <tr key={f.feature}>
                    <td className="py-2 pr-4 font-mono text-xs text-slate-800">
                      {featureLabel(f.feature)}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">{f.calls}</td>
                    <td className="py-2 pr-4 text-slate-600">
                      {f.totalTokens.toLocaleString()}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">{f.failedCalls}</td>
                    <td className="py-2 pr-4 text-slate-600">
                      {f.avgTokensPerCall.toLocaleString()}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">
                      {formatCost(f.avgCostPerCall)}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">
                      {formatCost(f.estimatedCostUsd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="No AI usage events recorded. Generate a blueprint or run a Pro tool to capture data." />
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>Cost by model</SectionTitle>
        {m.aiUsage.modelBreakdown.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-3">
            {m.aiUsage.modelBreakdown.map((mdl) => (
              <div
                key={mdl.model}
                className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
              >
                <span className="font-mono font-semibold text-slate-800">
                  {mdl.model}
                </span>
                <span className="ml-2 text-slate-500">
                  {mdl.calls} calls · {mdl.totalTokens.toLocaleString()} tokens · {formatCost(mdl.estimatedCostUsd)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState message="No model usage data yet." />
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>Highest-cost events</SectionTitle>
        {m.drilldowns.highCostEvents.length > 0 ? (
          <div className="mt-3 grid gap-2">
            {m.drilldowns.highCostEvents.map((e) => (
              <div
                key={e.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-xs"
              >
                <div className="flex flex-wrap gap-2">
                  <span className="font-mono font-semibold text-slate-800">
                    {featureLabel(e.feature)}
                  </span>
                  <span className="text-slate-500">{e.model}</span>
                  {e.email ? (
                    <Link
                      to={e.userId ? `/admin/users/${encodeURIComponent(e.userId)}` : "#"}
                      className="text-indigo-600 hover:text-indigo-800"
                    >
                      {e.email}
                    </Link>
                  ) : null}
                </div>
                <div className="flex gap-3 text-slate-500">
                  {e.totalTokens !== null ? (
                    <span>{e.totalTokens.toLocaleString()} tokens</span>
                  ) : null}
                  <span className="font-semibold text-slate-800">
                    {formatCost(e.estimatedTotalCostUsd)}
                  </span>
                  <span className="text-slate-400">{formatDate(e.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState message="No high-cost events in this range." />
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>Recent AI events</SectionTitle>
        {m.drilldowns.aiEvents.length > 0 ? (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-left text-xs">
              <thead>
                <tr className="border-b border-slate-100">
                  {["Feature", "Model", "Status", "Tokens", "Est. cost", "User", "Time"].map((h) => (
                    <th key={h} className="py-2 pr-3 font-semibold text-slate-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {m.drilldowns.aiEvents.map((e) => (
                  <tr key={e.id}>
                    <td className="py-2 pr-3 font-mono text-slate-800">
                      {featureLabel(e.feature)}
                    </td>
                    <td className="py-2 pr-3 text-slate-600">{e.model}</td>
                    <td className="py-2 pr-3">{statusBadge(e.requestStatus)}</td>
                    <td className="py-2 pr-3 text-slate-600">
                      {e.totalTokens?.toLocaleString() ?? "—"}
                    </td>
                    <td className="py-2 pr-3 text-slate-600">
                      {formatCost(e.estimatedTotalCostUsd)}
                    </td>
                    <td className="py-2 pr-3">
                      {e.email && e.userId ? (
                        <Link
                          to={`/admin/users/${encodeURIComponent(e.userId)}`}
                          className="text-indigo-600 hover:text-indigo-800"
                        >
                          {e.email}
                        </Link>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-slate-400">
                      {formatDate(e.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="No AI usage events in this filter." />
        )}
      </section>
    </div>
  );
}

function UsersTab({ m }: { m: AdminMetricsOverviewResponse }) {
  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>Top users by estimated AI cost</SectionTitle>
        <CostDisclaimer />
        {m.drilldowns.topUsersByAiCost.length > 0 ? (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[540px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  {["User", "Tier", "Calls", "Tokens", "Est. cost"].map((h) => (
                    <th key={h} className="py-2 pr-4 font-semibold text-slate-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {m.drilldowns.topUsersByAiCost.map((u, i) => (
                  <tr key={u.userId ?? i}>
                    <td className="py-2 pr-4">
                      {u.userId ? (
                        <Link
                          to={`/admin/users/${encodeURIComponent(u.userId)}`}
                          className="font-semibold text-indigo-600 hover:text-indigo-800"
                        >
                          {u.name ?? u.email ?? u.userId}
                        </Link>
                      ) : (
                        <span className="text-slate-400">Unknown user</span>
                      )}
                      {u.email ? (
                        <p className="text-xs text-slate-400">{u.email}</p>
                      ) : null}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">
                      {tierLabel(u.tier)}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">{u.calls}</td>
                    <td className="py-2 pr-4 text-slate-600">
                      {u.totalTokens.toLocaleString()}
                    </td>
                    <td className="py-2 pr-4 font-semibold text-slate-800">
                      {formatCost(u.estimatedCostUsd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="No AI usage data for this filter." />
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>Top users by blueprints generated</SectionTitle>
        {m.drilldowns.topUsersByBlueprints.length > 0 ? (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[480px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  {["User", "Tier", "Blueprints", "Generated"].map((h) => (
                    <th key={h} className="py-2 pr-4 font-semibold text-slate-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {m.drilldowns.topUsersByBlueprints.map((u, i) => (
                  <tr key={u.userId ?? i}>
                    <td className="py-2 pr-4">
                      {u.userId ? (
                        <Link
                          to={`/admin/users/${encodeURIComponent(u.userId)}`}
                          className="font-semibold text-indigo-600 hover:text-indigo-800"
                        >
                          {u.name ?? u.email ?? u.userId}
                        </Link>
                      ) : (
                        <span className="text-slate-400">Unknown</span>
                      )}
                      {u.email ? (
                        <p className="text-xs text-slate-400">{u.email}</p>
                      ) : null}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">
                      {tierLabel(u.tier)}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">
                      {u.blueprintCount}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">
                      {u.generatedBlueprintCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="No blueprint data for this filter." />
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>Top access codes by usage</SectionTitle>
        {m.accessCodes.topCodes.length > 0 ? (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[400px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  {["Code", "Tier", "Uses / max", "Status"].map((h) => (
                    <th key={h} className="py-2 pr-4 font-semibold text-slate-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {m.accessCodes.topCodes.map((c) => (
                  <tr key={c.code}>
                    <td className="py-2 pr-4 font-mono text-xs text-slate-800">
                      {c.code}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">
                      {tierLabel(c.tierGranted)}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">
                      {c.usesCount}{c.maxUses !== null ? ` / ${c.maxUses}` : " / ∞"}
                    </td>
                    <td className="py-2 pr-4">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${c.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}
                      >
                        {c.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="No access codes created yet." />
        )}
      </section>
    </div>
  );
}

function ActivityTab({ m }: { m: AdminMetricsOverviewResponse }) {
  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>Daily AI usage</SectionTitle>
        <CostDisclaimer />
        {m.drilldowns.dailyAiUsage.length > 0 ? (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[500px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  {["Date", "Calls", "Failed", "Tokens", "Est. cost"].map((h) => (
                    <th key={h} className="py-2 pr-4 font-semibold text-slate-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {m.drilldowns.dailyAiUsage.map((d) => (
                  <tr key={d.date}>
                    <td className="py-2 pr-4 font-semibold text-slate-800">
                      {formatShortDate(d.date)}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">{d.calls}</td>
                    <td className="py-2 pr-4 text-slate-600">
                      {d.failedCalls > 0 ? (
                        <span className="font-semibold text-red-600">
                          {d.failedCalls}
                        </span>
                      ) : (
                        "0"
                      )}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">
                      {d.totalTokens.toLocaleString()}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">
                      {formatCost(d.estimatedCostUsd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="No AI usage events in this range." />
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>Access code redemptions by day</SectionTitle>
        {m.drilldowns.accessCodeRedemptionsByDay.length > 0 ? (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[280px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="py-2 pr-4 font-semibold text-slate-700">Date</th>
                  <th className="py-2 pr-4 font-semibold text-slate-700">Redemptions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {m.drilldowns.accessCodeRedemptionsByDay.map((d) => (
                  <tr key={d.date}>
                    <td className="py-2 pr-4 font-semibold text-slate-800">
                      {formatShortDate(d.date)}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">{d.redemptions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="No access code redemptions in this range." />
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>Purchase failures by day</SectionTitle>
        {m.drilldowns.purchaseFailuresByDay.length > 0 ? (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[280px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="py-2 pr-4 font-semibold text-slate-700">Date</th>
                  <th className="py-2 pr-4 font-semibold text-slate-700">Failures</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {m.drilldowns.purchaseFailuresByDay.map((d) => (
                  <tr key={d.date}>
                    <td className="py-2 pr-4 font-semibold text-slate-800">
                      {formatShortDate(d.date)}
                    </td>
                    <td className="py-2 pr-4">
                      <span className="font-semibold text-red-600">
                        {d.failures}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="No purchase failures in this range." />
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <SectionTitle>Recent purchase failures</SectionTitle>
        {m.purchases.recentFailures.length > 0 ? (
          <div className="mt-3 grid gap-2">
            {m.purchases.recentFailures.map((f) => (
              <div
                key={f.id}
                className="rounded-md border border-red-100 bg-red-50 p-3 text-xs"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-red-800">
                    {f.provider}
                  </span>
                  {f.eventType ? (
                    <span className="text-red-600">{f.eventType}</span>
                  ) : null}
                  {f.email ? (
                    <span className="text-red-600">{f.email}</span>
                  ) : null}
                  <span className="ml-auto text-red-400">
                    {formatDate(f.createdAt)}
                  </span>
                </div>
                {f.errorMessage ? (
                  <p className="mt-1 text-red-700">{f.errorMessage}</p>
                ) : null}
                <Link
                  to={`/admin/purchase-events/${encodeURIComponent(f.id)}`}
                  className="mt-1 inline-flex font-semibold text-red-600 hover:text-red-800"
                >
                  View event →
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState message="No purchase failures in this range." />
        )}
      </section>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export function AdminMetrics() {
  const { getToken, isLoaded } = useAuth();
  const [range, setRange] = useState<MetricsRange>("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [featureFilter, setFeatureFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [data, setData] = useState<AdminMetricsOverviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDenied, setIsDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filters = useMemo<MetricsFilters>(
    () => ({
      feature: featureFilter || undefined,
      email: submittedEmail || undefined,
      tier: tierFilter || undefined,
      startDate: range === "custom" ? customStart || undefined : undefined,
      endDate: range === "custom" ? customEnd || undefined : undefined,
    }),
    [range, featureFilter, submittedEmail, tierFilter, customStart, customEnd],
  );

  useEffect(() => {
    async function loadMetrics() {
      if (!isLoaded) return;
      if (range === "custom" && (!customStart || !customEnd)) return;

      setIsLoading(true);
      setIsDenied(false);
      setError(null);

      try {
        const token = await getToken();
        setData(await getAdminMetricsOverview(token, range, filters));
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 401 || err.status === 403 || err.status === 404)
        ) {
          setIsDenied(true);
          return;
        }
        setError(
          err instanceof Error ? err.message : "Unable to load metrics.",
        );
      } finally {
        setIsLoading(false);
      }
    }

    void loadMetrics();
  }, [range, filters, getToken, isLoaded]);

  function resetFilters() {
    setEmailFilter("");
    setSubmittedEmail("");
    setFeatureFilter("");
    setTierFilter("");
  }

  if (isDenied) return <AccessDenied />;

  const tabs: { value: Tab; label: string }[] = [
    { value: "overview", label: "Overview" },
    { value: "ai", label: "AI Usage" },
    { value: "users", label: "Users" },
    { value: "activity", label: "Activity" },
  ];

  const hasActiveFilters = submittedEmail || featureFilter || tierFilter;

  return (
    <div className="space-y-6">
      <Link
        to="/admin"
        className="inline-flex text-sm font-semibold text-indigo-700 hover:text-indigo-900"
      >
        Back to Admin
      </Link>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-sm font-semibold text-indigo-700">Admin</p>
        <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
          Metrics
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
          Read-only overview of product usage, access activity, purchase events
          and estimated AI cost.
        </p>
      </section>

      {/* Range selector */}
      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setRange(opt.value)}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${
                range === opt.value
                  ? "bg-indigo-600 text-white"
                  : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
              }`}
            >
              {opt.label}
            </button>
          ))}
          {data ? (
            <span className="ml-2 text-xs text-slate-400">
              Updated {new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(new Date(data.generatedAt))}
            </span>
          ) : null}
        </div>

        {range === "custom" ? (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
            <span className="text-sm text-slate-500">to</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
        ) : null}
      </section>

      {/* Filters */}
      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <form
          className="grid gap-3 lg:grid-cols-[1fr_200px_180px_auto_auto]"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmittedEmail(emailFilter.trim());
          }}
        >
          <input
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            placeholder="Filter by email…"
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          />
          <select
            value={featureFilter}
            onChange={(e) => setFeatureFilter(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            {FEATURE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            {TIER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
          >
            Apply
          </button>
          {hasActiveFilters ? (
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              Reset
            </button>
          ) : null}
        </form>
        {hasActiveFilters ? (
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
            {submittedEmail ? <span>email: {submittedEmail}</span> : null}
            {featureFilter ? (
              <span>feature: {featureLabel(featureFilter)}</span>
            ) : null}
            {tierFilter ? <span>tier: {tierLabel(tierFilter)}</span> : null}
          </div>
        ) : null}
      </section>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {isLoading ? (
        <div className="space-y-4">
          <div className="h-32 animate-pulse rounded-lg bg-white" />
          <div className="h-48 animate-pulse rounded-lg bg-white" />
        </div>
      ) : data ? (
        <>
          {/* Tabs */}
          <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveTab(tab.value)}
                className={`flex-1 rounded-md py-2 text-sm font-semibold transition ${
                  activeTab === tab.value
                    ? "bg-indigo-600 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "overview" ? <OverviewTab m={data} /> : null}
          {activeTab === "ai" ? <AiTab m={data} /> : null}
          {activeTab === "users" ? <UsersTab m={data} /> : null}
          {activeTab === "activity" ? <ActivityTab m={data} /> : null}
        </>
      ) : null}
    </div>
  );
}
